/**
 * Auth-host helpers — derive the Windows Auth host from the current hostname.
 *
 * Extracted from LoginPage so the re-auth flow (lib/reauth.ts) and axiosClient
 * can share the exact same domain logic. LoginPage re-exports these for
 * backward compatibility.
 *
 * Mirrors the AngularJS `getBaseDomain` / `getAuthHost` helpers in
 * public/SPIGlassApp/Login/login.js and services/authService.js.
 */

/**
 * Extract the registrable parent domain from a hostname so we can build the
 * `authentication.{base}` host. `app.sgpdev.com` → `sgpdev.com`,
 * `localhost` → `localhost`.
 */
export function getBaseDomain(hostname: string = window.location.hostname): string {
  if (!hostname) return '';
  const lastDot = hostname.lastIndexOf('.');
  if (lastDot === -1) return hostname;
  const secondLastDot = hostname.lastIndexOf('.', lastDot - 1);
  if (secondLastDot === -1) return hostname;
  return hostname.substring(secondLastDot + 1);
}

/**
 * Build the Windows Auth host. Pure localhost has no `authentication.*`
 * sibling, so it falls back to the local IdP port.
 */
export function getAuthHost(hostname?: string): string {
  const base = getBaseDomain(hostname);
  if (!base || base === 'localhost') return 'localhost:44300';
  return 'authentication.' + base;
}

/**
 * Full `https://authentication.{domain}/windows` URL — the Windows Auth
 * endpoint that mints a fresh `.{domain}`-scoped Bearer cookie.
 */
export function getWindowsAuthUrl(hostname?: string): string {
  return 'https://' + getAuthHost(hostname) + '/windows';
}
